import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart3,
  Users,
  TrendingUp,
  Euro,
  Target,
  Megaphone,
  Settings,
  HeadphonesIcon,
  LogOut,
  Menu,
  X,
  Eye,
  Edit3,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
  RefreshCw,
  MessageSquare,
  Send
} from 'lucide-react';
import CampaignNotificationPopup from '../components/CampaignNotificationPopup';

const menuItems = [
  { id: 'overview', label: 'Übersicht', icon: BarChart3 },
  { id: 'campaigns', label: 'Kampagnen', icon: Megaphone },
  { id: 'users', label: 'User', icon: Users },
  { id: 'support', label: 'Support', icon: HeadphonesIcon },
  { id: 'google-ads', label: 'Google Ads', icon: TrendingUp },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [notificationCampaign, setNotificationCampaign] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [linkingCampaign, setLinkingCampaign] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Support Tab States
  const [supportSubTab, setSupportSubTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedSection, setExpandedSection] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeView === 'support') {
      if (supportSubTab === 'tickets') {
        fetchTickets();
      } else {
        fetchCallbacks();
      }
    }
  }, [activeView, supportSubTab]);

  const handleMenuClick = (viewId) => {
    setActiveView(viewId);
    if (viewId === 'google-ads') {
      navigate('/google-ads');
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const loadDashboardData = async () => {
    try {
      // SMART CACHING: Load cached data immediately (instant UI)
      const CACHE_KEY = 'admin_dashboard_cache';
      const CACHE_TIMESTAMP_KEY = 'admin_dashboard_cache_timestamp';
      const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
      
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      const now = Date.now();
      
      // Show cached data immediately if available and recent
      if (cachedData && cacheTimestamp) {
        const age = now - parseInt(cacheTimestamp);
        if (age < CACHE_DURATION) {
          try {
            const data = JSON.parse(cachedData);
            setStatistics(data.statistics);
            setUsers(data.users);
            setAllCampaigns(data.campaigns);
            setGoogleAdsAccounts(data.google_ads_accounts);
            setLoading(false); // Show data immediately!
            console.log('✅ Loaded from cache (instant)');
          } catch (e) {
            console.error('Cache parse error:', e);
          }
        }
      }
      
      // COMBINED ENDPOINT: Fetch fresh data in background (1 request instead of 4!)
      const response = await fetch(`${backendUrl}/api/admin/dashboard-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const freshData = await response.json();
      
      // Update UI with fresh data
      setStatistics(freshData.statistics);
      setUsers(freshData.users);
      setAllCampaigns(freshData.campaigns);
      setGoogleAdsAccounts(freshData.google_ads_accounts);
      
      // Update cache with fresh data
      localStorage.setItem(CACHE_KEY, JSON.stringify(freshData));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, now.toString());
      
      setLoading(false);
      console.log('✅ Fresh data loaded and cached');
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  const loadUserCampaigns = async (userId) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${userId}/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserCampaigns(data);
        setSelectedUser(userId);
      }
    } catch (error) {
      console.error('Error loading user campaigns:', error);
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        const statusMessages = {
          'active': '✅ Benutzer erfolgreich aktiviert',
          'paused': '⏸️ Benutzer wurde pausiert',
          'canceled': '❌ Benutzer wurde gekündigt'
        };
        console.log(statusMessages[newStatus] || 'Status erfolgreich geändert');
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      console.log('❌ Fehler beim Status-Update');
    }
  };

  const updateUserSubscription = async (userId, subscriptionType) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription_type: subscriptionType })
      });
      
      if (res.ok) {
        const subscriptionMessages = {
          'starter': '📦 Abo erfolgreich auf Starter geändert',
          'growth': '📈 Abo erfolgreich auf Growth geändert',
          'pro': '🚀 Abo erfolgreich auf Pro geändert'
        };
        console.log(subscriptionMessages[subscriptionType] || 'Abo erfolgreich geändert');
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      console.log('❌ Fehler beim Abo-Update');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        console.log('🗑️ Benutzer erfolgreich gelöscht');
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      console.log('❌ Fehler beim Löschen');
    }
  };

  const updateCampaignStatus = async (campaignId, newStatus) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/campaigns/${campaignId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        const statusMessages = {
          'aktiv': '🚀 Kampagne erfolgreich live geschaltet!',
          'paused': '⏸️ Kampagne wurde pausiert',
          'in_bearbeitung': '📝 Kampagne in Bearbeitung gesetzt',
          'neu': '📋 Kampagne als neu markiert'
        };
        console.log(statusMessages[newStatus] || 'Status erfolgreich aktualisiert');
        
        if (selectedUser) {
          loadUserCampaigns(selectedUser);
        }
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      console.log('❌ Fehler beim Status-Update');
    }
  };

  const showCampaignNotification = (campaign) => {
    setNotificationCampaign(campaign);
    // Auto-close after 15 seconds
    setTimeout(() => {
      setNotificationCampaign(null);
    }, 15000);
  };

  const linkCampaignToGoogleAds = async (campaignId, googleAdsCustomerId) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/campaigns/${campaignId}/link-google-ads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          google_ads_customer_id: googleAdsCustomerId || null
        })
      });
      
      if (res.ok) {
        console.log('✅ Verknüpfung erfolgreich aktualisiert');
        loadDashboardData();
        setLinkingCampaign(null);
      } else {
        console.log('❌ Fehler beim Verknüpfen');
      }
    } catch (error) {
      console.error('Error linking campaign:', error);
      console.log('❌ Fehler beim Verknüpfen');
    }
  };

  // Support Functions
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTickets(data.tickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCallbacks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/support/callbacks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCallbacks(data.callbacks);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTicketDetails(data);
      setSelectedTicket(ticketId);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const addReply = async (e) => {
    e.preventDefault();
    
    if (!replyMessage.trim()) {
      return;
    }

    try {
      setLoading(true);
      await fetch(`${backendUrl}/api/support/tickets/${selectedTicket}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyMessage })
      });
      
      console.log('Antwort gesendet');
      setReplyMessage('');
      fetchTicketDetails(selectedTicket);
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await fetch(`${backendUrl}/api/support/tickets/${ticketId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      console.log('Status aktualisiert');
      if (selectedTicket === ticketId) {
        fetchTicketDetails(ticketId);
      }
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const updateCallbackStatus = async (callbackId, status) => {
    try {
      await fetch(`${backendUrl}/api/support/callbacks/${callbackId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      console.log('Status aktualisiert');
      fetchCallbacks();
    } catch (error) {
      console.error('Error updating callback status:', error);
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Möchten Sie dieses Ticket wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      await fetch(`${backendUrl}/api/support/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Ticket erfolgreich gelöscht');
      setSelectedTicket(null);
      setTicketDetails(null);
      fetchTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const deleteCallback = async (callbackId) => {
    if (!window.confirm('Möchten Sie diesen Rückruf wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      await fetch(`${backendUrl}/api/support/callbacks/${callbackId}/admin`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Rückruf erfolgreich gelöscht');
      setSelectedCallback(null);
      fetchCallbacks();
    } catch (error) {
      console.error('Error deleting callback:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      pending: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-red-100 text-red-800',
      neu: 'bg-gray-100 text-gray-800',
      aktiv: 'bg-green-100 text-green-800',
      in_bearbeitung: 'bg-yellow-100 text-yellow-800'
    };

    const labels = {
      open: 'Offen',
      in_progress: 'In Bearbeitung',
      resolved: 'Gelöst',
      pending: 'Ausstehend',
      completed: 'Erledigt',
      active: 'Aktiv',
      paused: 'Pausiert',
      neu: 'Neu',
      aktiv: 'Aktiv',
      in_bearbeitung: 'In Bearbeitung'
    };

    return (
      <Badge className={styles[status]}>
        {labels[status] || status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter === 'all') return true;
    return ticket.status === statusFilter;
  });

  const filteredCallbacks = callbacks.filter(callback => {
    if (statusFilter === 'all') return true;
    return callback.status === statusFilter;
  });

  if (loading && !statistics && users.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md border border-gray-200"
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-700" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700" />
        )}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${mobileMenuOpen ? 'block' : 'hidden'}
          lg:block
          fixed lg:sticky
          top-0 left-0 lg:left-auto
          h-screen z-40
          w-[240px] bg-white
          transition-all duration-300 ease-in-out
          rounded-r-[28px]
          flex flex-col
          overflow-hidden
        `}
      >
        {/* Logo */}
        <div className="p-6 pb-2">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="AdSimplify Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold text-gray-900">
              Admin Panel
            </span>
          </div>
        </div>

        {/* Made in Österreich */}
        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-gray-500">
            Made with ❤️ in Österreich.
          </p>
        </div>

        {/* Navigation */}
        <nav className="px-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-xl
                  transition-all duration-200
                  ${
                    isActive
                      ? 'bg-[#E6EDFF] text-[#0040FF]'
                      : 'text-black hover:bg-gray-200/50'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-semibold text-[15px]">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Bottom Section */}
        <div className="p-4">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center space-x-3 px-4 py-3"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-semibold text-[15px]">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 lg:p-8">
          {activeView === 'overview' && (
            <div className="space-y-8">
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Admin Übersicht
                </h1>
                <p className="text-gray-600">
                  Willkommen im Admin Panel - Hier ist eine Übersicht über Ihre Plattform.
                </p>
              </div>

              {/* Statistics Cards */}
              {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Campaigns Overview */}
                  <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0040FF] flex items-center justify-center">
                          <Megaphone className="w-5 h-5 text-white" />
                        </div>
                        Kampagnen
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Aktiv</span>
                        <span className="text-2xl font-bold text-green-600">
                          {statistics.campaigns.active}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Pending</span>
                        <span className="text-2xl font-bold text-yellow-600">
                          {statistics.campaigns.pending}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Pausiert</span>
                        <span className="text-2xl font-bold text-gray-600">
                          {statistics.campaigns.paused}
                        </span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">Gesamt</span>
                        <span className="text-3xl font-bold text-[#0040FF]">
                          {statistics.campaigns.total}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Revenue Card */}
                  <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                          <Euro className="w-5 h-5 text-white" />
                        </div>
                        Umsatz
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">
                        €{statistics.revenue.total.toFixed(2)}
                      </div>
                      <p className="text-sm text-gray-600">Gesamteinnahmen</p>
                    </CardContent>
                  </Card>

                  {/* Users Card */}
                  <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        Benutzer
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">
                        {users.length}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Registrierte Kunden</p>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Aktiv:</span>
                        <span className="font-semibold">
                          {users.filter(u => u.status === 'active').length}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* New Campaign Requests */}
              {statistics?.new_requests && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center">
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      Neue Kampagnen-Anfragen
                    </CardTitle>
                    <CardDescription>
                      Kampagnen die eine Genehmigung benötigen
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {statistics.new_requests.length > 0 ? (
                      <div className="space-y-4">
                        {statistics.new_requests.map((campaign) => (
                          <Card key={campaign.id} className="border-l-4 border-l-orange-500">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-lg">{campaign.company_name}</h4>
                                  <p className="text-sm text-gray-600 mb-1">{campaign.users?.email}</p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(campaign.created_at).toLocaleDateString('de-DE')}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setNotificationCampaign(campaign)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Details
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => updateCampaignStatus(campaign.id, 'aktiv')}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Genehmigen
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => updateCampaignStatus(campaign.id, 'paused')}
                                  >
                                    <AlertCircle className="w-4 h-4 mr-1" />
                                    Ablehnen
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Target className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Keine neuen Anfragen</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Support Tab */}
          {activeView === 'support' && (
            <div className="space-y-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Support</h1>
                <p className="text-gray-600">Tickets und Rückrufanfragen verwalten</p>
              </div>

              {/* Sub-Tabs */}
              <div className="flex space-x-1 p-1 bg-gray-100 rounded-lg w-fit">
                <Button
                  onClick={() => {
                    setSupportSubTab('tickets');
                    setSelectedTicket(null);
                    setTicketDetails(null);
                    setStatusFilter('all');
                  }}
                  variant={supportSubTab === 'tickets' ? 'default' : 'ghost'}
                  className="flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Support-Tickets
                </Button>
                <Button
                  onClick={() => {
                    setSupportSubTab('callbacks');
                    setSelectedTicket(null);
                    setTicketDetails(null);
                    setStatusFilter('all');
                  }}
                  variant={supportSubTab === 'callbacks' ? 'default' : 'ghost'}
                  className="flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Rückrufanfragen
                </Button>
              </div>

              {/* Tickets Tab */}
              {supportSubTab === 'tickets' && !selectedTicket && (
                <div className="space-y-6">
                  {/* Header with Filter */}
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold">Alle Support-Tickets</h3>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Status</SelectItem>
                        <SelectItem value="open">Offen</SelectItem>
                        <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                        <SelectItem value="resolved">Gelöst</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {tickets.filter(t => t.status === 'open').length}
                        </div>
                        <div className="text-sm text-blue-600 font-medium">Offene Tickets</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-yellow-600">
                          {tickets.filter(t => t.status === 'in_progress').length}
                        </div>
                        <div className="text-sm text-yellow-600 font-medium">In Bearbeitung</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-green-600">
                          {tickets.filter(t => t.status === 'resolved').length}
                        </div>
                        <div className="text-sm text-green-600 font-medium">Gelöst</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tickets List */}
                  {loading ? (
                    <div className="text-center py-12">Lädt...</div>
                  ) : filteredTickets.length === 0 ? (
                    <Card>
                      <CardContent className="text-center py-12">
                        <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Keine Tickets gefunden</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Betreff</TableHead>
                              <TableHead>Kunde</TableHead>
                              <TableHead>Erstellt</TableHead>
                              <TableHead>Letzte Aktivität</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Aktionen</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredTickets.map((ticket) => (
                              <TableRow key={ticket.id}>
                                <TableCell>
                                  <div className="font-semibold">{ticket.subject}</div>
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium">{ticket.users?.company_name || 'N/A'}</div>
                                  <div className="text-sm text-gray-500">{ticket.users?.email || 'N/A'}</div>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {formatDate(ticket.created_at)}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {formatDate(ticket.last_activity_at)}
                                </TableCell>
                                <TableCell>
                                  {getStatusBadge(ticket.status)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    onClick={() => fetchTicketDetails(ticket.id)}
                                    size="sm"
                                  >
                                    Öffnen
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Ticket Details */}
              {supportSubTab === 'tickets' && selectedTicket && ticketDetails && (
                <div className="space-y-6">
                  <Button
                    onClick={() => {
                      setSelectedTicket(null);
                      setTicketDetails(null);
                    }}
                    variant="outline"
                  >
                    ← Zurück zur Übersicht
                  </Button>

                  {/* Ticket Header */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-2xl mb-3">{ticketDetails.ticket.subject}</CardTitle>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-500" />
                              <span className="font-semibold">{ticketDetails.ticket.users?.company_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">{ticketDetails.ticket.users?.email}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>Erstellt: {formatDate(ticketDetails.ticket.created_at)}</span>
                            <span>Letzte Aktivität: {formatDate(ticketDetails.ticket.last_activity_at)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          {getStatusBadge(ticketDetails.ticket.status)}
                          <Select
                            value={ticketDetails.ticket.status}
                            onValueChange={(value) => updateTicketStatus(selectedTicket, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Offen</SelectItem>
                              <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                              <SelectItem value="resolved">Gelöst</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={() => deleteTicket(selectedTicket)}
                            variant="destructive"
                            size="sm"
                          >
                            Ticket löschen
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Messages Thread */}
                  <div className="space-y-4">
                    {ticketDetails.messages.map((message) => (
                      <Card
                        key={message.id}
                        className={message.is_admin_reply
                          ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 ml-8'
                          : 'bg-white mr-8'}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-semibold text-sm">
                              {message.is_admin_reply ? '🛠️ Admin: ' + (message.users?.company_name || 'Support Team') : '👤 Kunde: ' + (message.users?.company_name || 'Unbekannt')}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                          </div>
                          <p className="text-gray-800 whitespace-pre-wrap">{message.message}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Reply Form */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Admin-Antwort hinzufügen</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={addReply} className="space-y-4">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Schreibe deine Antwort..."
                          rows={5}
                          required
                        />
                        <Button type="submit" disabled={loading}>
                          <Send className="w-4 h-4 mr-2" />
                          {loading ? 'Wird gesendet...' : 'Antwort senden'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Callbacks Tab */}
              {supportSubTab === 'callbacks' && !selectedCallback && (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold">Alle Rückrufanfragen</h3>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Status</SelectItem>
                        <SelectItem value="pending">Ausstehend</SelectItem>
                        <SelectItem value="completed">Erledigt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-orange-600">
                          {callbacks.filter(c => c.status === 'pending').length}
                        </div>
                        <div className="text-sm text-orange-600 font-medium">Ausstehende Rückrufe</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-3xl font-bold text-green-600">
                          {callbacks.filter(c => c.status === 'completed').length}
                        </div>
                        <div className="text-sm text-green-600 font-medium">Erledigte Rückrufe</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Callbacks List */}
                  {loading ? (
                    <div className="text-center py-12">Lädt...</div>
                  ) : filteredCallbacks.length === 0 ? (
                    <Card>
                      <CardContent className="text-center py-12">
                        <Phone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Keine Rückrufanfragen gefunden</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Kunde</TableHead>
                              <TableHead>Telefonnummer</TableHead>
                              <TableHead>Gewünschter Termin</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Aktionen</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredCallbacks.map((callback) => (
                              <TableRow key={callback.id}>
                                <TableCell>
                                  <div className="font-medium">{callback.users?.company_name || 'N/A'}</div>
                                  <div className="text-sm text-gray-500">{callback.users?.email || 'N/A'}</div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-gray-500" />
                                    <span>{callback.phone_number}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">
                                    <div>{new Date(callback.preferred_date).toLocaleDateString('de-DE')}</div>
                                    <div className="text-gray-500">{callback.preferred_time}</div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {getStatusBadge(callback.status)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => setSelectedCallback(callback)}
                                      size="sm"
                                      variant="outline"
                                    >
                                      Details
                                    </Button>
                                    {callback.status === 'pending' && (
                                      <Button
                                        onClick={() => updateCallbackStatus(callback.id, 'completed')}
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        Erledigt
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Callback Details */}
              {supportSubTab === 'callbacks' && selectedCallback && (
                <div className="space-y-6">
                  <Button
                    onClick={() => setSelectedCallback(null)}
                    variant="outline"
                  >
                    ← Zurück zur Übersicht
                  </Button>

                  <Card>
                    <CardHeader>
                      <CardTitle>Rückrufanfrage Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Customer Info */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Kunde</h4>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-lg">{selectedCallback.users?.company_name || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">{selectedCallback.users?.email || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Date & Time */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Gewünschter Termin</h4>
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <div>
                            <div className="font-semibold text-lg">
                              {new Date(selectedCallback.preferred_date).toLocaleDateString('de-DE', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-sm text-gray-600">Uhrzeit: {selectedCallback.preferred_time}</div>
                          </div>
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Telefonnummer</h4>
                        <div className="flex items-center gap-2">
                          <Phone className="w-5 h-5 text-green-600" />
                          <a href={`tel:${selectedCallback.phone_number}`} className="text-lg font-semibold text-blue-600 hover:underline">
                            {selectedCallback.phone_number}
                          </a>
                        </div>
                      </div>

                      {/* Note */}
                      {selectedCallback.note && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Notiz des Kunden</h4>
                          <Card className="bg-gray-50">
                            <CardContent className="p-4">
                              <p>{selectedCallback.note}</p>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Created */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">Erstellt am</h4>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">{formatDate(selectedCallback.created_at)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-4">
                        {getStatusBadge(selectedCallback.status)}
                        {selectedCallback.status === 'pending' && (
                          <Button
                            onClick={() => {
                              updateCallbackStatus(selectedCallback.id, 'completed');
                              setSelectedCallback(null);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Als erledigt markieren
                          </Button>
                        )}
                        <Button
                          onClick={() => deleteCallback(selectedCallback.id)}
                          variant="destructive"
                        >
                          Rückruf löschen
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Campaigns Tab */}
          {activeView === 'campaigns' && (
            <div className="space-y-8">
              {selectedCampaign ? (
                // Campaign Detail View - New Design
                <div className="space-y-6">
                  <Button
                    onClick={() => setSelectedCampaign(null)}
                    variant="outline"
                    className="mb-6"
                  >
                    ← Zurück zur Liste
                  </Button>
                  
                  {/* Hero Section - Most Important Info */}
                  <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          {/* Company Name */}
                          <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            {selectedCampaign.company_name}
                          </h1>
                          
                          {/* Contact Info */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {selectedCampaign.user_email}
                            </div>
                            <div className="flex items-center gap-1">
                              <Target className="w-4 h-4" />
                              {selectedCampaign.location}
                            </div>
                          </div>
                          
                          {/* Budget & Campaign Type */}
                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                              💰 {selectedCampaign.budget ? `${selectedCampaign.budget} €` : `${selectedCampaign.daily_budget} € / Tag`}
                            </Badge>
                            <Badge className={selectedCampaign.laufzeit === 'monatlich' 
                              ? 'bg-blue-600 text-white text-sm px-3 py-1'
                              : 'bg-orange-600 text-white text-sm px-3 py-1'}>
                              {selectedCampaign.laufzeit === 'monatlich' ? '🔄 Abo (monatlich)' : '⚡ Einmalige Kampagne'}
                            </Badge>
                          </div>
                          
                          {/* Expert Optimization Badge */}
                          {selectedCampaign.expert_optimization && (
                            <Badge className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-sm px-3 py-1 mb-3">
                              ⭐ EXPERTEN-OPTIMIERUNG AKTIV
                            </Badge>
                          )}
                        </div>
                        
                        {/* Status Badge - Top Right */}
                        <div className="ml-4">
                          {getStatusBadge(selectedCampaign.status)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Info Cards Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Company Info Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Edit3 className="w-5 h-5 text-blue-600" />
                          Unternehmen
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Company Description */}
                        <div>
                          <label className="text-sm font-medium text-gray-600">Beschreibung</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-lg border">
                            <p className="text-sm text-gray-800">
                              {selectedCampaign.beschreibung || "Keine Beschreibung verfügbar"}
                            </p>
                          </div>
                        </div>
                        
                        {/* Industry & Contact */}
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Branche</label>
                            <p className="text-sm mt-1">{selectedCampaign.branche || 'Nicht angegeben'}</p>
                          </div>
                          
                          {selectedCampaign.website_url && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Website</label>
                              <a 
                                href={selectedCampaign.website_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                              >
                                {selectedCampaign.website_url}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                          
                          {selectedCampaign.phone_number && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Telefon</label>
                              <a 
                                href={`tel:${selectedCampaign.phone_number}`}
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                              >
                                {selectedCampaign.phone_number}
                                <Phone className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Campaign Info Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-green-600" />
                          Kampagne
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Goals */}
                        <div>
                          <label className="text-sm font-medium text-gray-600">Ziele</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-lg border">
                            <p className="text-sm text-gray-800">{selectedCampaign.goal || selectedCampaign.ziele}</p>
                          </div>
                        </div>
                        
                        {/* Campaign Details */}
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Budget Details</label>
                            <div className="mt-1 space-y-1">
                              <p className="text-sm">Budget: <span className="font-semibold text-green-600">
                                {selectedCampaign.budget ? `${selectedCampaign.budget} €` : `${selectedCampaign.daily_budget} € / Tag`}
                              </span></p>
                              {selectedCampaign.expert_optimization && (
                                <p className="text-sm">Experten-Optimierung: <span className="font-semibold text-yellow-600">+29 € (einmalig)</span></p>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium text-gray-600">Erstellt am</label>
                            <p className="text-sm mt-1">{new Date(selectedCampaign.created_at).toLocaleDateString('de-DE')}</p>
                          </div>
                          
                          {selectedCampaign.werbestandorte && (
                            <div>
                              <label className="text-sm font-medium text-gray-600">Werbestandorte</label>
                              <p className="text-sm mt-1">{selectedCampaign.werbestandorte}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Collapsible Sections */}
                  <div className="space-y-4">
                    {/* Ad Texts Collapsible */}
                    <Card>
                      <CardHeader className="cursor-pointer" onClick={() => setExpandedSection(expandedSection === 'ads' ? '' : 'ads')}>
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Edit3 className="w-5 h-5 text-blue-600" />
                            📝 Anzeigentexte & Creative Assets
                          </div>
                          <Button variant="ghost" size="sm">
                            {expandedSection === 'ads' ? '▲ Einklappen' : '▼ Aufklappen'}
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      
                      {expandedSection === 'ads' && (
                        <CardContent className="space-y-4">
                          {/* Headlines */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Anzeigentitel (max. 30 Zeichen)</h4>
                            <div className="space-y-3">
                              {[1, 2, 3, 4, 5].map((num) => {
                                const headline = selectedCampaign[`headline${num}`];
                                if (!headline) return null;
                                return (
                                  <div key={num} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                    <div className="flex justify-between items-start mb-2">
                                      <span className="text-sm font-medium text-blue-900">Titel {num}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-blue-700">{headline.length}/30</span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            navigator.clipboard.writeText(headline);
                                            console.log(`Anzeigentitel ${num} kopiert!`);
                                          }}
                                        >
                                          <Copy className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                    <p className="text-sm font-medium text-gray-900">{headline}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Descriptions */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Anzeigenbeschreibungen (max. 90 Zeichen)</h4>
                            <div className="space-y-3">
                              {[selectedCampaign.ad_text, selectedCampaign.description2].filter(Boolean).map((desc, index) => (
                                <div key={index} className="bg-green-50 p-3 rounded-lg border border-green-200">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-medium text-green-900">Beschreibung {index + 1}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-green-700">{desc.length}/90</span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          navigator.clipboard.writeText(desc);
                                          console.log(`Beschreibung ${index + 1} kopiert!`);
                                        }}
                                      >
                                        <Copy className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-900">{desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>

                    {/* Campaign Settings Collapsible */}
                    <Card>
                      <CardHeader className="cursor-pointer" onClick={() => setExpandedSection(expandedSection === 'settings' ? '' : 'settings')}>
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings className="w-5 h-5 text-gray-600" />
                            ⚙️ Kampagnen-Einstellungen
                          </div>
                          <Button variant="ghost" size="sm">
                            {expandedSection === 'settings' ? '▲ Einklappen' : '▼ Aufklappen'}
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      
                      {expandedSection === 'settings' && (
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Status ändern</label>
                              <Select
                                value={selectedCampaign.status}
                                onValueChange={(value) => {
                                  updateCampaignStatus(selectedCampaign.id, value);
                                  setSelectedCampaign({...selectedCampaign, status: value});
                                }}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="neu">Neu</SelectItem>
                                  <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                                  <SelectItem value="aktiv">Aktiv</SelectItem>
                                  <SelectItem value="paused">Pausiert</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <label className="text-sm font-medium text-gray-700">Kampagnen-ID</label>
                              <div className="mt-1 p-2 bg-gray-50 rounded border text-xs font-mono">
                                {selectedCampaign.id}
                              </div>
                            </div>
                          </div>
                          
                          <Separator className="my-4" />
                          
                          {/* Google Ads Integration */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-green-600" />
                              Google Ads Kampagne verknüpfen
                            </h4>
                            
                            {selectedCampaign.google_ads_customer_id ? (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-green-900 mb-1">✅ Verknüpft</p>
                                    {(() => {
                                      const linkedAccount = googleAdsAccounts.find(
                                        acc => acc.customer_id === selectedCampaign.google_ads_customer_id
                                      );
                                      return (
                                        <div className="space-y-1">
                                          {linkedAccount && (
                                            <p className="text-sm font-semibold text-green-800">
                                              {linkedAccount.descriptive_name}
                                            </p>
                                          )}
                                          <p className="text-xs text-green-700">
                                            Konto-ID: <span className="font-mono font-semibold">{selectedCampaign.google_ads_customer_id}</span>
                                          </p>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setLinkingCampaign(selectedCampaign.id)}
                                  >
                                    Ändern
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-yellow-900 mb-1">⚠️ Nicht verknüpft</p>
                                    <p className="text-xs text-yellow-700">
                                      Diese Kampagne ist noch nicht mit einem Google Ads Konto verbunden.
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => setLinkingCampaign(selectedCampaign.id)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    Verknüpfen
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  </div>

                  {/* Action Buttons Footer */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => updateCampaignStatus(selectedCampaign.id, 'aktiv')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          🚀 Live schalten
                        </Button>
                        <Button
                          onClick={() => updateCampaignStatus(selectedCampaign.id, 'paused')}
                          variant="outline"
                        >
                          ⏸️ Pausieren
                        </Button>
                        <Button
                          onClick={() => updateCampaignStatus(selectedCampaign.id, 'in_bearbeitung')}
                          variant="outline"
                        >
                          ✏️ In Bearbeitung
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setNotificationCampaign(selectedCampaign)}
                        >
                          📊 Quick View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                // Campaigns List
                <div className="space-y-6">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Kampagnen</h1>
                    <p className="text-gray-600">Alle Kampagnen mit Details und Anzeigentexten</p>
                  </div>

                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Firma</TableHead>
                            <TableHead>Kunde</TableHead>
                            <TableHead>Standort</TableHead>
                            <TableHead>Budget</TableHead>
                            <TableHead>Anzeigentitel</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allCampaigns.map((campaign) => (
                            <TableRow key={campaign.id} className="hover:bg-gray-50">
                              <TableCell>
                                <div>
                                  <div className="font-semibold">{campaign.company_name}</div>
                                  <div className="text-sm text-gray-500">{campaign.goal}</div>
                                </div>
                              </TableCell>
                              <TableCell>{campaign.user_email}</TableCell>
                              <TableCell>{campaign.location}</TableCell>
                              <TableCell className="font-semibold">
                                {campaign.budget ? `${campaign.budget} €` : `${campaign.daily_budget} €/Tag`}
                              </TableCell>
                              <TableCell>
                                <div className="max-w-xs">
                                  {campaign.headline1 ? (
                                    <>
                                      <div className="font-semibold truncate" title={campaign.headline1}>
                                        {campaign.headline1}
                                      </div>
                                      {campaign.headline2 && (
                                        <div className="text-gray-600 truncate text-sm mt-1" title={campaign.headline2}>
                                          {campaign.headline2}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-gray-400 italic text-sm">Nicht festgelegt</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(campaign.status)}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setNotificationCampaign(campaign)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Quick View
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => setSelectedCampaign(campaign)}
                                  >
                                    Details →
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeView === 'users' && (
            <div className="space-y-6">
              {selectedUser ? (
                // User Campaigns View
                <div>
                  <Button
                    onClick={() => setSelectedUser(null)}
                    variant="outline"
                    className="mb-6"
                  >
                    ← Zurück zur Benutzerliste
                  </Button>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Kampagnen des Benutzers</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Firma</TableHead>
                            <TableHead>Standort</TableHead>
                            <TableHead>Budget</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Erstellt</TableHead>
                            <TableHead>Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userCampaigns.map((campaign) => (
                            <TableRow key={campaign.id}>
                              <TableCell className="font-semibold">{campaign.company_name}</TableCell>
                              <TableCell>{campaign.location}</TableCell>
                              <TableCell className="font-semibold">
                                {campaign.budget ? `${campaign.budget} €` : `${campaign.daily_budget} €/Tag`}
                              </TableCell>
                              <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                              <TableCell>{new Date(campaign.created_at).toLocaleDateString('de-DE')}</TableCell>
                              <TableCell>
                                <Select
                                  value={campaign.status}
                                  onValueChange={(value) => updateCampaignStatus(campaign.id, value)}
                                >
                                  <SelectTrigger className="w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="neu">Neu</SelectItem>
                                    <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                                    <SelectItem value="aktiv">Aktiv</SelectItem>
                                    <SelectItem value="paused">Pausiert</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                // Users List
                <div className="space-y-6">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Kundenverwaltung</h1>
                    <p className="text-gray-600">Verwalten Sie alle registrierten Kunden</p>
                  </div>

                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>E-Mail</TableHead>
                            <TableHead>Firma</TableHead>
                            <TableHead>Paket</TableHead>
                            <TableHead>Kampagnen</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Nächste Zahlung</TableHead>
                            <TableHead>Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((user) => (
                            <TableRow key={user.id} className="hover:bg-gray-50">
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4 text-gray-400" />
                                  {user.email}
                                </div>
                              </TableCell>
                              <TableCell className="font-semibold">
                                {user.company_name || 'Nicht angegeben'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={user.subscription_type === 'pro' ? 'default' : 'secondary'}>
                                  {user.subscription_type}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => loadUserCampaigns(user.id)}
                                >
                                  {user.campaign_count || 0} Kampagnen
                                </Button>
                              </TableCell>
                              <TableCell>{getStatusBadge(user.status)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm">
                                    {user.next_payment_date 
                                      ? new Date(user.next_payment_date).toLocaleDateString('de-DE')
                                      : 'Nicht festgelegt'
                                    }
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Select
                                    value={user.status}
                                    onValueChange={(value) => updateUserStatus(user.id, value)}
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="active">Aktiv</SelectItem>
                                      <SelectItem value="paused">Pausiert</SelectItem>
                                      <SelectItem value="canceled">Gekündigt</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => deleteUser(user.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Campaign Notification Popup */}
      {notificationCampaign && (
        <CampaignNotificationPopup
          campaign={notificationCampaign}
          onClose={() => setNotificationCampaign(null)}
        />
      )}
      
      {/* Google Ads Linking Dialog */}
      {linkingCampaign && (
        <Dialog open={!!linkingCampaign} onOpenChange={() => setLinkingCampaign(null)}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Google Ads Kampagne verknüpfen</DialogTitle>
              <DialogDescription>
                Wählen Sie ein Google Ads Konto aus, um die Kampagne zu verknüpfen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {googleAdsAccounts.length > 0 ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Google Ads Konto auswählen
                    </label>
                    <Select
                      onValueChange={(value) => {
                        linkCampaignToGoogleAds(linkingCampaign, value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Konto auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {googleAdsAccounts.map((account) => (
                          <SelectItem key={account.customer_id} value={account.customer_id}>
                            {account.descriptive_name} ({account.customer_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        linkCampaignToGoogleAds(linkingCampaign, null);
                      }}
                      className="w-full"
                    >
                      Verknüpfung entfernen
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-600">Keine Google Ads Konten verfügbar</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Bitte verknüpfen Sie zuerst ein Google Ads Konto im Google Ads Tab.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setLinkingCampaign(null)}
              >
                Abbrechen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminPanel;